// Watchers (T-132, docs/WATCHERS_PAGE_SPEC_2026_10_04.md): every watcher, worker, agent and cron in one list.
// Name, one sentence, last ran, whether it burns tokens, and Run / Pause / Details / Log. Data: GET /api/watchers (shared/watcher_api.py).
// T-254 (2026-10-13): a banner at the top says what is running right now and how much longer (components/RunningIndicator.jsx), and a running row shows its own time left.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { watchers as api } from '../lib/api'
import { Panel, MONO } from '../components/deskParts'
import { STATUS_TONE, formatAge, formatDuration, tokenLabel, groupWatchers, attentionSummary, PAUSE_CHOICES, KIND_LABEL, itemLine } from '../lib/watchers'
import { RunningBanner } from '../components/RunningIndicator'

const btn = { ...MONO, fontSize: '0.64rem', letterSpacing: '0.06em', padding: '3px 8px', borderRadius: 4, border: '1px solid #334155', color: '#cbd5e1', background: 'transparent', cursor: 'pointer' }
const btnOff = { ...btn, color: '#475569', borderColor: '#1f2937', cursor: 'not-allowed' }

function Chip({ status }) {
  const c = STATUS_TONE[status] || '#94a3b8'
  return <span style={{ ...MONO, fontSize: '0.62rem', color: c, border: `1px solid ${c}`, borderRadius: 3, padding: '1px 6px', letterSpacing: '0.06em' }}>{status}</span>
}

function SidePanel({ title, onClose, children }) {
  return (
    <div role="dialog" aria-label={title} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 100vw)', background: '#0b1220', borderLeft: '1px solid #1f2937', zIndex: 60, display: 'flex', flexDirection: 'column' }}>
      <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: '1px solid #1f2937' }}>
        <span style={{ ...MONO, fontSize: '0.75rem', color: '#e2e8f0' }}>{title}</span>
        <span className="flex-1" />
        <button style={btn} onClick={onClose}>CLOSE</button>
      </div>
      <div className="flex-1 overflow-auto p-3 space-y-3" style={{ fontSize: '0.8rem', color: '#cbd5e1' }}>{children}</div>
    </div>
  )
}

function LogPanel({ w, onClose }) {
  const [d, setD] = useState(null)
  const [err, setErr] = useState(null)
  useEffect(() => { api.log(w.id).then(r => (r.success ? setD(r) : setErr(r.error || 'failed'))).catch(e => setErr(String(e.message || e))) }, [w.id])
  return (
    <SidePanel title={`Log · ${w.name}`} onClose={onClose}>
      {err && <p style={{ color: '#fca5a5' }}>{err}</p>}
      {!d && !err && <p>Loading…</p>}
      {d && (<>
        {d.railway_command && <p style={{ ...MONO, fontSize: '0.7rem' }}>Raw log: <code style={{ color: '#7dd3fc' }}>{d.railway_command}</code></p>}
        <h4 style={{ ...MONO, fontSize: '0.65rem', color: '#64748b' }}>LAST RUNS</h4>
        {d.runs.length === 0 ? <p>No runs recorded yet (history starts when this page's backend is deployed).</p> : (
          <table style={{ ...MONO, fontSize: '0.68rem', width: '100%' }}><tbody>
            {d.runs.map((r, i) => (
              <tr key={i}><td style={{ color: '#94a3b8' }}>{new Date(r.finished_at).toLocaleString()}</td><td><Chip status={r.status} /></td><td>{formatDuration(r.duration_ms)}</td><td style={{ color: '#94a3b8' }}>{r.message}</td></tr>
            ))}
          </tbody></table>
        )}
        <h4 style={{ ...MONO, fontSize: '0.65rem', color: '#64748b' }}>ERRORS, LAST 7 DAYS</h4>
        {d.errors.length === 0 ? <p>None.</p> : d.errors.map((e, i) => <p key={i} style={{ ...MONO, fontSize: '0.68rem' }}><span style={{ color: '#94a3b8' }}>{new Date(e.time).toLocaleString()}</span> <b>{e.type}</b> {e.message}</p>)}
        <h4 style={{ ...MONO, fontSize: '0.65rem', color: '#64748b' }}>OPEN ALERTS</h4>
        {d.alerts.length === 0 ? <p>None.</p> : d.alerts.map((a, i) => <p key={i} style={{ ...MONO, fontSize: '0.68rem' }}>[{a.severity}] {a.title}</p>)}
      </>)}
    </SidePanel>
  )
}

function DetailsPanel({ w, onClose, onChanged }) {
  const [text, setText] = useState('')
  const [msg, setMsg] = useState(null)
  const [reqs, setReqs] = useState([])
  useEffect(() => { api.log(w.id).then(r => r.success && setReqs(r.requests || [])).catch(() => {}) }, [w.id])
  const send = async () => {
    setMsg(null)
    try {
      const r = await api.request(w.id, text)
      if (r.success) { setMsg('Saved. Your next Claude session will see it; nothing is changed until then.'); setText(''); setReqs(prev => [r.request, ...prev]); onChanged() }
      else setMsg(r.error || 'failed')
    } catch (e) { setMsg(String(e.message || e)) }
  }
  const t = tokenLabel(w.tokens)
  return (
    <SidePanel title={`Details · ${w.name}`} onClose={onClose}>
      <p>{w.description}</p>
      <table style={{ ...MONO, fontSize: '0.7rem', width: '100%' }}><tbody>
        <tr><td style={{ color: '#64748b' }}>id</td><td>{w.id}</td></tr>
        <tr><td style={{ color: '#64748b' }}>kind · host</td><td>{KIND_LABEL[w.kind] || w.kind} · {w.host}</td></tr>
        <tr><td style={{ color: '#64748b' }}>schedule</td><td>{w.schedule}</td></tr>
        <tr><td style={{ color: '#64748b' }}>burns tokens</td><td>{t.text}</td></tr>
        <tr><td style={{ color: '#64748b' }}>settings</td><td>{w.settings.length ? w.settings.join(', ') : 'none in system_config'}</td></tr>
        <tr><td style={{ color: '#64748b' }}>pausable</td><td>{w.pausable ? 'yes (max 24 hours, then it resumes by itself)' : `no: ${w.pause_reason || 'not allowed'}`}</td></tr>
        {w.note && <tr><td style={{ color: '#64748b' }}>note</td><td>{w.note}</td></tr>}
      </tbody></table>
      <div style={{ borderTop: '1px solid #1f2937', paddingTop: 10 }}>
        <p style={{ ...MONO, fontSize: '0.65rem', color: '#64748b' }}>ASK CLAUDE TO CHANGE THIS <span style={{ color: '#fbbf24' }}>· uses Claude (tokens) when your next session handles it</span></p>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={4} maxLength={2000} placeholder="Describe the change, for example: run this every 5 minutes instead of every 10."
          style={{ width: '100%', background: '#111827', border: '1px solid #334155', borderRadius: 4, color: '#e2e8f0', padding: 8, fontSize: '0.8rem' }} />
        <div className="flex items-center gap-3 mt-2">
          <button style={text.trim() ? btn : btnOff} disabled={!text.trim()} onClick={send}>SEND REQUEST</button>
          {msg && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{msg}</span>}
        </div>
        <p style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 6 }}>This queues a request. No change is made from this page and no AI is called here; a Claude session reads the queue, makes the change under the usual review, and marks it done.</p>
      </div>
      {reqs.length > 0 && (<div>
        <h4 style={{ ...MONO, fontSize: '0.65rem', color: '#64748b' }}>REQUESTS</h4>
        {reqs.map(r => <p key={r.id} style={{ fontSize: '0.75rem' }}>[{r.status}] {r.body}{r.resolution ? ` — ${r.resolution}` : ''}</p>)}
      </div>)}
    </SidePanel>
  )
}

export default function Watchers() {
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [kind, setKind] = useState('all')
  const [query, setQuery] = useState('')
  const [panel, setPanel] = useState(null)       // {type: 'log'|'details', w}
  const [note, setNote] = useState(null)         // result of Run / Pause
  const [pauseMin, setPauseMin] = useState(240)

  const [fetchedAt, setFetchedAt] = useState(() => Date.now())   // T-254: when `data.running` was fetched, so a row's time left can count down
  const load = useCallback(() => {
    api.list().then(r => { if (r.success) { setData(r); setFetchedAt(Date.now()); setErr(null) } else setErr(r.error || 'failed') }).catch(e => setErr(String(e.message || e)))
  }, [])
  useEffect(() => {
    load()
    const id = setInterval(() => { if (!document.hidden) load() }, 30_000)
    return () => clearInterval(id)
  }, [load])

  const groups = useMemo(() => groupWatchers(data?.watchers, kind, query), [data, kind, query])
  const run = async (w) => {
    let confirm
    if (w.run_confirm) { confirm = window.prompt(`This job can place orders. Type "${w.id}" to run it now.`); if (confirm !== w.id) return }
    else if (!window.confirm(`Run "${w.name}" once, now?`)) return
    setNote(`Running ${w.name}…`)
    try { const r = await api.run(w.id, confirm); setNote(`${w.name}: ${r.success ? 'finished' : 'failed'}${r.output ? `\n${r.output}` : ''}${r.error ? `\n${r.error}` : ''}`) }
    catch (e) { setNote(`${w.name}: ${e.message || e}`) }
    load()
  }
  const pause = async (w) => {
    if (w.paused_until) { try { await api.resume(w.id) } catch (e) { setNote(String(e.message || e)) } load(); return }
    if (!window.confirm(`Pause "${w.name}" for ${PAUSE_CHOICES.find(([m]) => m === pauseMin)?.[1] || pauseMin + ' min'}?\n\n${w.description}\n\nIt resumes by itself when the time is up. The pause is logged and shown on this page.`)) return
    try { const r = await api.pause(w.id, pauseMin); if (!r.success) setNote(r.error) } catch (e) { setNote(String(e.message || e)) }
    load()
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h1 style={{ ...MONO, fontSize: '0.95rem', color: '#e2e8f0' }}>Watchers</h1>
        <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{data ? attentionSummary(data.counts) : 'loading…'}</span>
        <span className="flex-1" />
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="search" aria-label="search watchers"
          style={{ ...MONO, fontSize: '0.7rem', background: '#111827', border: '1px solid #334155', borderRadius: 4, color: '#e2e8f0', padding: '3px 8px' }} />
        <select value={kind} onChange={e => setKind(e.target.value)} aria-label="kind" style={{ ...MONO, fontSize: '0.7rem', background: '#111827', border: '1px solid #334155', borderRadius: 4, color: '#e2e8f0', padding: '3px 6px' }}>
          <option value="all">all kinds</option>
          {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label style={{ ...MONO, fontSize: '0.65rem', color: '#64748b' }}>pause for&nbsp;
          <select value={pauseMin} onChange={e => setPauseMin(Number(e.target.value))} style={{ ...MONO, fontSize: '0.7rem', background: '#111827', border: '1px solid #334155', borderRadius: 4, color: '#e2e8f0', padding: '3px 6px' }}>
            {PAUSE_CHOICES.map(([m, l]) => <option key={m} value={m}>{l}</option>)}
          </select>
        </label>
      </div>
      <RunningBanner />
      {err && <p style={{ color: '#fca5a5', fontSize: '0.8rem' }}>Could not load: {err}</p>}
      {note && <pre style={{ ...MONO, fontSize: '0.68rem', background: '#111827', border: '1px solid #334155', borderRadius: 4, padding: 8, whiteSpace: 'pre-wrap', color: '#cbd5e1' }} onClick={() => setNote(null)}>{note}{'\n'}(click to dismiss)</pre>}
      {groups.map(g => (
        <Panel key={g.kind} title={`${g.label} · ${g.rows.length}`} bodyStyle={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 880, fontSize: '0.78rem', borderCollapse: 'collapse' }}>
              <thead><tr style={{ ...MONO, fontSize: '0.6rem', color: '#64748b', textAlign: 'left' }}>
                <th style={{ padding: '4px 8px' }}>NAME</th><th>WHAT IT DOES</th><th>LAST RAN</th><th>TOKENS</th><th>STATUS</th><th style={{ textAlign: 'right', paddingRight: 8 }}>ACTIONS</th>
              </tr></thead>
              <tbody>
                {g.rows.map(w => {
                  const t = tokenLabel(w.tokens)
                  return (
                    <tr key={w.id} style={{ borderTop: '1px solid #1f2937', verticalAlign: 'top', opacity: w.enabled ? 1 : 0.55 }}>
                      <td style={{ padding: '6px 8px', minWidth: 150 }}><div style={{ color: '#e2e8f0' }}>{w.name}</div><div style={{ ...MONO, fontSize: '0.6rem', color: '#64748b' }}>{w.id} · {w.schedule}</div></td>
                      <td style={{ color: '#cbd5e1', maxWidth: 360 }}>{w.description}</td>
                      <td style={{ ...MONO, fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                        {w.last ? (<><div>{formatAge(w.age_s)}</div><div style={{ color: '#64748b' }}>{w.last.status}{w.last.duration_ms != null ? ` · ${formatDuration(w.last.duration_ms)}` : ''}</div></>) : <span style={{ color: '#64748b' }}>not reported</span>}
                        {w.paused_until && <div style={{ color: '#60a5fa' }}>until {new Date(w.paused_until).toLocaleTimeString()}</div>}
                        {w.running && w.running.state !== 'STALLED' && <div style={{ color: STATUS_TONE.RUNNING }}>{itemLine(w.running, fetchedAt, Date.now())}</div>}
                      </td>
                      <td style={{ ...MONO, fontSize: '0.68rem', color: t.tone === 'bad' ? '#fb7185' : t.tone === 'warn' ? '#fbbf24' : '#94a3b8' }}>{t.text}</td>
                      <td><Chip status={w.running && w.running.state === 'RUNNING' ? 'RUNNING' : w.status} /></td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap', padding: '6px 8px' }}>
                        <span className="inline-flex gap-1.5 flex-wrap justify-end">
                          <button style={w.can_run ? btn : btnOff} disabled={!w.can_run} onClick={() => run(w)} title={w.can_run ? 'Run once now' : (w.run_hint || 'No on-demand run for this watcher')}>RUN</button>
                          <button style={(w.pausable || w.paused_until) ? btn : btnOff} disabled={!(w.pausable || w.paused_until)} onClick={() => pause(w)}
                            title={(w.pausable || w.paused_until) ? (w.paused_until ? 'Resume now' : 'Pause (it resumes by itself)') : `Cannot be paused: ${w.pause_reason || 'not allowed'}`}>{w.paused_until ? 'RESUME' : 'PAUSE'}</button>
                          <button style={btn} onClick={() => setPanel({ type: 'details', w })}>DETAILS{w.open_requests ? ` (${w.open_requests})` : ''}</button>
                          <button style={btn} onClick={() => setPanel({ type: 'log', w })}>LOG</button>
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      ))}
      {data && groups.length === 0 && <p style={{ color: '#64748b', fontSize: '0.8rem' }}>No watcher matches.</p>}
      {panel?.type === 'log' && <LogPanel w={panel.w} onClose={() => setPanel(null)} />}
      {panel?.type === 'details' && <DetailsPanel w={panel.w} onClose={() => setPanel(null)} onChanged={load} />}
    </div>
  )
}
